import * as React from "react";
import {
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { API } from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import { Alert } from "@material-ui/lab";
import { LoadingSpinner } from "../../../../utils/LoadingSpinner";
import { AMAZON_ID } from "../../../../consts/Patterns";
import { IReplanSchedulesRequest } from "./request/ReplanSchedulesRequest";

// Built as a list so adding a program later (e.g. AP_AUTO_REFILL) is a one-line change.
const PROGRAM_NAMES = [
  { value: "AP_PILLPACK", label: "AP_PILLPACK" },
  // { value: "AP_AUTO_REFILL", label: "AP_AUTO_REFILL (AR Order)" },
];

const DEFAULT_PROGRAM_NAME = "AP_PILLPACK";

interface IState {
  errorMessage: string;
  isLoading: boolean;
  request: IReplanSchedulesRequest;
  response: any;
}

enum InputField {
  CONTRACT_ID,
  CONTRACT_VERSION,
  PROGRAM_NAME,
}

class ReplanSchedulesSection extends React.Component<{}, IState> {
  constructor(props: {}) {
    super(props);

    this.state = {
      errorMessage: "",
      isLoading: false,
      request: { programName: DEFAULT_PROGRAM_NAME } as IReplanSchedulesRequest,
      response: "",
    };

    this.setRequestFields = this.setRequestFields.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleValidation = this.handleValidation.bind(this);
  }

  public render() {
    return (
      <Grid
        container
        direction="column"
        justify="center"
        alignItems="center"
        spacing={3}
      >
        <Grid item xs>
          <Alert severity="warning">
            This triggers a real Packets order "cancel + recreate" with no confirmation step. The commit is irreversible once submitted.
          </Alert>
        </Grid>

        <Grid item xs>
          <Alert severity="info">
            SMS decides Replan vs Recreate from the schedule's benefit states. A real order "cancel + recreate" only happens when the schedule has a COMPLETED benefit with an active order. For contracts whose benefits are all ACTIVE / UPCOMING / PENDING_MANUAL_ORDER (no COMPLETED), this runs the pre-order Replan path and cancels nothing — useful for testing the flow safely, but not the cancel path itself.
          </Alert>
        </Grid>

        <Grid item xs>
          <TextField
            style={{ width: 400 }}
            label="Contract Id (Required)"
            variant="outlined"
            value={this.state.request.contractId || ""}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              this.setRequestFields(InputField.CONTRACT_ID, event.target.value)
            }
          />
        </Grid>

        <Grid item xs>
          <TextField
            style={{ width: 400 }}
            label="Contract Version (Optional)"
            helperText="Leave blank to auto-derive from the active schedule"
            variant="outlined"
            value={this.state.request.contractVersion || ""}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              this.setRequestFields(InputField.CONTRACT_VERSION, event.target.value)
            }
          />
        </Grid>

        <Grid item xs>
          <FormControl variant="outlined" style={{ width: 400 }}>
            <Select
              value={this.state.request.programName || ""}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                this.setRequestFields(InputField.PROGRAM_NAME, event.target.value as string)
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Program Name (Required)
              </MenuItem>
              {PROGRAM_NAMES.map((program) => (
                <MenuItem key={program.value} value={program.value}>
                  {program.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs>
          <Button
            variant="outlined"
            color="default"
            onClick={this.handleSubmit}
            disabled={this.state.isLoading}
          >
            Submit
          </Button>
        </Grid>

        {this.state.isLoading && <LoadingSpinner />}

        {this.state.errorMessage && (
          <Alert variant="filled" severity="error">
            Error encountered: {this.state.errorMessage}
          </Alert>
        )}

        {this.state.response && (
          <div>
            <br />
            <h4>Status</h4>
            <p
              style={{
                border: "3px solid lightgray",
                padding: "5px",
                whiteSpace: "pre-wrap",
                wordBreak: "normal",
                wordWrap: "break-word",
              }}
            >
              <JsonRenderComponent
                jsonObject={this.state.response}
                printHtml={false}
              />
            </p>
          </div>
        )}
      </Grid>
    );
  }

  private handleValidation() {
    const errors: string[] = [];
    if (!this.state.request.contractId) {
      errors.push("Please enter the contract id");
    } else if (!this.state.request.contractId.match(AMAZON_ID)) {
      errors.push("Please enter a valid contract id");
    }
    if (!this.state.request.programName) {
      errors.push("Please enter the program name");
    }
    if (errors.length > 0) {
      alert(errors.join("\n"));
      return false;
    }
    return true;
  }

  private setRequestFields(field: InputField, value: string) {
    const updatedRequest: IReplanSchedulesRequest = { ...this.state.request };

    switch (field) {
      case InputField.CONTRACT_ID: {
        updatedRequest.contractId = value;
        break;
      }
      case InputField.CONTRACT_VERSION: {
        updatedRequest.contractVersion = value;
        break;
      }
      case InputField.PROGRAM_NAME: {
        updatedRequest.programName = value;
        break;
      }
      default: {
        break;
      }
    }
    this.setState({ request: updatedRequest });
  }

  private handleSubmit() {
    if (!this.handleValidation()) {
      return;
    }

    // Clear any prior response/error so a stale success isn't shown next to the new spinner.
    this.setState({ isLoading: true, errorMessage: "", response: "" });

    const body: IReplanSchedulesRequest = {
      contractId: this.state.request.contractId,
      programName: this.state.request.programName,
    };
    // Only send contractVersion when QA supplied one; blank means "auto-derive".
    if (this.state.request.contractVersion) {
      body.contractVersion = this.state.request.contractVersion;
    }
    const config = {
      body,
      headers: {},
    };
    this.callReplanSchedules(config);
  }

  private callReplanSchedules(config: any) {
    const apiName = "ApaqPortalAPI";
    const path = "/pharmacy-customer/replan-schedules";

    API.post(apiName, path, config)
      .then((getResponse: any) => {
        this.setState({ response: getResponse, errorMessage: "", isLoading: false });
      })
      .catch((error) => {
        this.setState({
          response: "",
          errorMessage: error?.response?.data?.message ?? "Request failed",
          isLoading: false,
        });
      });
  }
}

export default ReplanSchedulesSection;
